// WorkbenchThreatsPane — Module 5 centre-pane for the "Threats"
// section. Shows the scenario's attached threats with their mode,
// agent count, and airframe. Attach / detach from the preset library
// (or tenant-owned threats). Clicking a threat row opens its inline
// preview polyline overlay on the shared Leaflet map. Full editor is
// still surfaced at /plan/threats; this pane keeps the Workbench flow
// tight for scenario assembly.
import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, X as XIcon, Plus } from "lucide-react";
import { toast } from "sonner";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { formatApiError } from "@/lib/formatApiError";
import EmptyState from "@/components/EmptyState";
import SpinnerButton from "@/components/SpinnerButton";
import CredibilityBadge from "@/components/CredibilityBadge";
import JinkScheduleEditor from "@/components/threats/JinkScheduleEditor";
import { sim } from "@/api/endpoints";

const MapEditor = lazy(() => import("@/components/terrain/MapEditor"));
const NONE = "__none__";

const MODE_LABELS = {
    single_waypoint: "Single-ship waypoint",
    swarm: "Swarm",
    evasive: "Evasive",
    comms_denied: "Comms-denied",
};

export default function WorkbenchThreatsPane({ scenario, readOnly = false, onScenarioChanged }) {
    const scenarioId = scenario?.id;
    const [library, setLibrary] = useState([]);
    const [attached, setAttached] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [preview, setPreview] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);

    // Load library once.
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const list = await sim.threats.list({ scope: "tenant" });
                if (alive) setLibrary(Array.isArray(list) ? list : []);
            } catch (e) {
                toast.error(formatApiError(e, "Threats load failed"));
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, []);

    // Hydrate attached threats from scenario.threat_ids.
    useEffect(() => {
        let alive = true;
        const ids = scenario?.threat_ids || [];
        if (ids.length === 0) {
            setAttached([]);
            return () => {};
        }
        Promise.all(ids.map((id) => sim.threats.get(id).catch(() => null)))
            .then((docs) => { if (alive) setAttached(docs.filter(Boolean)); });
        return () => { alive = false; };
    }, [scenario?.threat_ids]);

    const loadPreview = useCallback(
        async (threatId) => {
            if (!threatId) {
                setPreview(null);
                return;
            }
            setPreviewLoading(true);
            try {
                const p = await sim.threats.preview(threatId, {
                    horizon_s: 60,
                    dt_s: 0.25,
                    scenario_id: scenarioId,
                });
                setPreview(p);
            } catch (_e) {
                setPreview(null);
            } finally {
                setPreviewLoading(false);
            }
        },
        [scenarioId],
    );

    // Auto-pick the first attached threat if none selected.
    useEffect(() => {
        if (!selectedId && attached.length > 0) {
            setSelectedId(attached[0].id);
        }
        if (selectedId && !attached.find((t) => t.id === selectedId)) {
            setSelectedId(attached[0]?.id || null);
        }
    }, [attached, selectedId]);

    useEffect(() => {
        if (selectedId) loadPreview(selectedId);
    }, [selectedId, loadPreview]);

    const attachableOptions = useMemo(() => {
        const attachedSet = new Set(attached.map((t) => t.id));
        return library.filter((t) => !attachedSet.has(t.id));
    }, [library, attached]);

    const onAttach = async (val) => {
        if (readOnly || val === NONE) return;
        setBusy(true);
        try {
            await sim.scenarios.attachThreat(scenarioId, val);
            toast.success("Threat attached");
            onScenarioChanged?.();
        } catch (e) {
            toast.error(formatApiError(e, "Attach failed"));
        } finally {
            setBusy(false);
        }
    };

    const onDetach = async (threatId) => {
        if (readOnly) return;
        setBusy(true);
        try {
            await sim.scenarios.detachThreat(scenarioId, threatId);
            toast.success("Threat detached");
            onScenarioChanged?.();
        } catch (e) {
            toast.error(formatApiError(e, "Detach failed"));
        } finally {
            setBusy(false);
        }
    };

    const polylines = useMemo(() => {
        const out = [];
        for (const a of preview?.agents || []) {
            const ring = a.samples.map((s) => [s.lat, s.lon]);
            if (ring.length >= 2) out.push({ ring, id: a.agent_id });
        }
        return out;
    }, [preview]);

    const mapCentre = useMemo(() => {
        const sel = attached.find((t) => t.id === selectedId);
        if (sel?.spawn) return [sel.spawn.lat, sel.spawn.lon];
        if (attached[0]?.spawn) return [attached[0].spawn.lat, attached[0].spawn.lon];
        if (scenario?.terrain_ref) return [51.51, 0.0];
        return [51.51, 0.0];
    }, [attached, selectedId, scenario?.terrain_ref]);

    if (loading) {
        return <div className="text-xs text-ux-text-3">Loading threats…</div>;
    }

    return (
        <div className="space-y-4" data-testid="workbench-threats-pane">
            <div
                className="border border-ux-border-1 rounded-md bg-ux-bg-1 p-3 flex flex-wrap items-end gap-4"
                data-testid="workbench-threats-attach"
            >
                <label className="text-[11px] font-mono uppercase tracking-wider text-ux-text-3 space-y-1">
                    Attach threat
                    <Select
                        value={NONE}
                        onValueChange={onAttach}
                        disabled={busy || readOnly || attachableOptions.length === 0}
                    >
                        <SelectTrigger
                            className="w-[300px] bg-ux-bg-2 text-[12px] font-mono"
                            data-testid="workbench-threats-select"
                        >
                            <SelectValue
                                placeholder={
                                    attachableOptions.length === 0
                                        ? "— no attachable threats —"
                                        : "— choose —"
                                }
                            />
                        </SelectTrigger>
                        <SelectContent className="bg-ux-bg-1 border-ux-border-1">
                            <SelectItem value={NONE} className="text-[12px] font-mono">
                                — choose —
                            </SelectItem>
                            {attachableOptions.map((t) => (
                                <SelectItem
                                    key={t.id}
                                    value={t.id}
                                    className="text-[12px] font-mono"
                                    data-testid={`workbench-threats-option-${t.id}`}
                                >
                                    {t.name} · {MODE_LABELS[t.mode] || t.mode}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </label>
                <Link
                    to="/plan/threats"
                    className="ml-auto text-xs text-ux-accent underline decoration-dotted"
                >
                    Open threats library <ExternalLink className="inline w-3 h-3" />
                </Link>
            </div>

            {attached.length === 0 ? (
                <EmptyState
                    title="No threats attached"
                    body="Pick a preset threat above — up to 5 can run concurrently. /plan/threats supports deeper editing."
                />
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Left: attached threat cards */}
                    <div
                        className="border border-ux-border-1 rounded-md bg-ux-bg-1 p-3 space-y-2"
                        data-testid="workbench-threats-attached"
                    >
                        <div className="flex items-center justify-between">
                            <h4 className="font-mono text-xs uppercase tracking-wider text-ux-text-3">
                                {attached.length} attached
                            </h4>
                            {attached.length >= 5 ? (
                                <span className="text-[10px] font-mono text-ux-warning">
                                    at cap (5)
                                </span>
                            ) : null}
                        </div>
                        <ul className="space-y-2">
                            {attached.map((t) => {
                                const active = t.id === selectedId;
                                const count = Math.round((t.count || {}).value || 1);
                                return (
                                    <li
                                        key={t.id}
                                        className={`group flex items-start gap-2 px-2 py-2 rounded-md border ${
                                            active
                                                ? "border-ux-accent/60 bg-ux-accent/5"
                                                : "border-ux-border-1 hover:border-ux-border-2"
                                        }`}
                                        data-testid={`workbench-threat-card-${t.id}`}
                                    >
                                        <button
                                            type="button"
                                            className="flex-1 text-left"
                                            onClick={() => setSelectedId(t.id)}
                                            data-testid={`workbench-threat-select-${t.id}`}
                                        >
                                            <div className="flex items-center gap-2 text-sm">
                                                <span className="font-medium">{t.name}</span>
                                                <span className="text-[10px] font-mono uppercase tracking-wider text-ux-text-3">
                                                    {MODE_LABELS[t.mode] || t.mode}
                                                </span>
                                                <CredibilityBadge
                                                    entity={t}
                                                    testId={`workbench-threat-tier-${t.id}`}
                                                />
                                            </div>
                                            <div className="text-[11px] font-mono text-ux-text-3">
                                                {count}-ship · airframe {t.airframe_id}
                                            </div>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => onDetach(t.id)}
                                            className="text-ux-text-3 hover:text-ux-danger"
                                            data-testid={`workbench-threat-detach-${t.id}`}
                                            disabled={readOnly || busy}
                                            aria-label="Detach threat"
                                        >
                                            <XIcon className="h-3.5 w-3.5" />
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                        <div className="text-[11px] font-mono text-ux-text-3 pt-2">
                            {previewLoading
                                ? "Computing preview…"
                                : preview
                                ? `${preview.agent_count} agent${
                                      preview.agent_count === 1 ? "" : "s"
                                  } · ${preview.agents?.[0]?.samples?.length || 0} ticks`
                                : "Pick a threat card to preview its 60 s trajectory."}
                        </div>
                    </div>

                    <Suspense
                        fallback={
                            <div
                                data-testid="workbench-threats-map-loading"
                                className="h-[420px] rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center text-xs text-slate-500"
                            >
                                Loading map…
                            </div>
                        }
                    >
                        <MapEditor
                            centre={mapCentre}
                            zoom={12}
                            polylines={polylines}
                            obstacles={[]}
                            heightPx={420}
                            testId="workbench-threats-map"
                        />
                    </Suspense>
                </div>
            )}

            {/* Module 5 deferred — jink-schedule editor surfaces when the
                currently-selected threat is in evasive mode. Round-trips
                via PATCH evasive.jink_schedule[]. */}
            {(() => {
                const sel = attached.find((t) => t.id === selectedId);
                if (!sel || sel.mode !== "evasive") return null;
                return (
                    <JinkScheduleEditor
                        key={sel.id}
                        threat={sel}
                        readOnly={readOnly || busy}
                        onSaved={() => onScenarioChanged?.()}
                    />
                );
            })()}
        </div>
    );
}
